import * as XLSX from 'xlsx';

export interface StoredOrder {
  folio: string;
  fecha: string;
  hora: string;
  // Cliente
  clientName: string;
  phone: string;
  email: string;
  city: string;
  state: string;
  // Pedido
  sport: string;
  teamName: string;
  quantity: number;
  shirtStyle: string;
  estimatedPrice: string;
  // Diseño
  colors: string;
  hasLogo: string;
  logoDescription: string;
  logoImageBase64?: string;   // imagen del logo en base64
  hasNumbers: string;
  hasNames: string;
  // Entrega
  sizes: string;
  deliveryDate: string;
  referenceDescription: string;
  additionalNotes: string;
  // Estado
  status: 'Nuevo' | 'En proceso' | 'Entregado';
}

const STORAGE_KEY = 'dosdetres_orders';
const FOLIO_KEY = 'dosdetres_folio_counter';

// Genera el siguiente número de folio: DDT-YYYYMM-XXX
export function generateFolio(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const prefix = `DDT-${year}${month}`;

  // Leer el contador actual
  const stored = localStorage.getItem(FOLIO_KEY);
  let counter = 1;
  if (stored) {
    const parsed = JSON.parse(stored);
    // Si el mes cambió, reiniciar contador
    if (parsed.prefix === prefix) {
      counter = parsed.counter + 1;
    }
  }

  // Guardar contador actualizado
  localStorage.setItem(FOLIO_KEY, JSON.stringify({ prefix, counter }));

  return `${prefix}-${String(counter).padStart(3, '0')}`;
}

// Guardar un pedido
export function saveOrder(order: StoredOrder): void {
  const orders = getOrders();
  orders.unshift(order); // más reciente primero
  localStorage.setItem(STORAGE_KEY, JSON.stringify(orders));
}

// Obtener todos los pedidos
export function getOrders(): StoredOrder[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

// Actualizar estado de un pedido
export function updateOrderStatus(folio: string, status: StoredOrder['status']): void {
  const orders = getOrders();
  const idx = orders.findIndex((o) => o.folio === folio);
  if (idx !== -1) {
    orders[idx].status = status;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(orders));
  }
}

// Eliminar un pedido
export function deleteOrder(folio: string): void {
  const orders = getOrders().filter((o) => o.folio !== folio);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(orders));
}

// Descargar todos los pedidos como Excel
export function downloadOrdersExcel(orders: StoredOrder[]): void {
  const rows = orders.map((o) => ({
    'FOLIO': o.folio,
    'FECHA': o.fecha,
    'HORA': o.hora,
    'ESTADO': o.status,
    'CLIENTE': o.clientName,
    'TELÉFONO': o.phone,
    'EMAIL': o.email,
    'CIUDAD': o.city,
    'ESTADO/PROV': o.state,
    'DEPORTE/USO': o.sport,
    'EQUIPO/EMPRESA': o.teamName,
    'CANTIDAD': o.quantity,
    'ESTILO': o.shirtStyle,
    'PRECIO EST.': o.estimatedPrice,
    'COLORES': o.colors,
    'LOGO': o.hasLogo,
    'DESC. LOGO': o.logoDescription,
    'NÚMEROS': o.hasNumbers,
    'NOMBRES': o.hasNames,
    'TALLAS': o.sizes,
    'FECHA ENTREGA': o.deliveryDate,
    'REFERENCIA': o.referenceDescription,
    'NOTAS': o.additionalNotes,
  }));

  const ws = XLSX.utils.json_to_sheet(rows);

  // Ancho de columnas
  ws['!cols'] = [
    { wch: 18 }, { wch: 12 }, { wch: 10 }, { wch: 12 },
    { wch: 22 }, { wch: 15 }, { wch: 24 }, { wch: 14 },
    { wch: 14 }, { wch: 16 }, { wch: 20 }, { wch: 10 },
    { wch: 20 }, { wch: 14 }, { wch: 20 }, { wch: 20 },
    { wch: 20 }, { wch: 10 }, { wch: 10 }, { wch: 30 },
    { wch: 14 }, { wch: 30 }, { wch: 30 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Pedidos');

  const now = new Date();
  const filename = `DosDeTres_Pedidos_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.xlsx`;
  XLSX.writeFile(wb, filename);
}
